// Daten der Eigentümerliste: alle Tabellen je einmal (seitenweise) – keine Abfrage je Eigentümer
// (die bisherige Liste zählte Objekte mit zwei Abfragen je Eigentümer).

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { alleSeiten } from '../supabase/alleSeiten';
import type { EigentuemerRoh, EinheitEigRoh, EinheitRoh, MieterRoh, ObjektEigRoh, ObjektRoh } from './liste';
import type { VerguetungEintrag } from './verwaltung';
import type { Beteiligter } from './beteiligte';

export interface VerguetungRoh { eigentuemer_id: string; betrag_je_einheit: number | string; gueltig_ab: string }

/** Verlauf je Eigentümer aus eigentuemer_verguetung. */
export function verguetungJe(rows: readonly VerguetungRoh[]): Map<string, VerguetungEintrag[]> {
  const m = new Map<string, VerguetungEintrag[]>();
  for (const r of rows) {
    const betrag = Number(r.betrag_je_einheit);
    if (!Number.isFinite(betrag)) continue;
    m.set(r.eigentuemer_id, [...(m.get(r.eigentuemer_id) ?? []), { betrag, ab: String(r.gueltig_ab).slice(0, 10) }]);
  }
  return m;
}

/** Miteigentümer & Vertreter je Eigentümer – nur für den Export (Gruppe „Zustellung & Vertreter“). */
export async function ladeExportBeteiligte(ids: readonly string[]): Promise<Map<string, Beteiligter[]>> {
  const je = new Map<string, Beteiligter[]>();
  for (let i = 0; i < ids.length; i += 200) {
    const { data, error } = await supabase.from('eigentuemer_beteiligte')
      .select('id, eigentuemer_id, typ, art, name, email, telefon, anteil, zustellvertreter, umfang, gueltig_bis, dokument_id')
      .in('eigentuemer_id', ids.slice(i, i + 200)).order('created_at', { ascending: true });
    if (error) throw new Error(error.message);
    for (const r of (data ?? []) as (Beteiligter & { eigentuemer_id: string; anteil: number | string | null })[]) {
      const { eigentuemer_id, ...b } = r;
      je.set(eigentuemer_id, [...(je.get(eigentuemer_id) ?? []), { ...b, anteil: b.anteil === null ? null : Number(b.anteil) }]);
    }
  }
  return je;
}

export interface EigListeDaten {
  /** vollständige Zeilen (select *) – auch für den Export */
  eigentuemer: (EigentuemerRoh & Record<string, unknown>)[];
  objektEig: ObjektEigRoh[];
  einheitEig: EinheitEigRoh[];
  objekte: ObjektRoh[];
  einheiten: EinheitRoh[];
  mieter: MieterRoh[];
  /** profiles.status je E-Mail (klein) für Rolle eigentuemer */
  portal: Map<string, string | null>;
  /** Vergütungshistorie je Eigentümer */
  verguetungen: Map<string, VerguetungEintrag[]>;
}

export type EigListeZustand = { status: 'laedt' } | { status: 'fehler'; meldung: string } | { status: 'ok'; daten: EigListeDaten };

const MIETER_FELDER = 'id, einheit_id, objekt_id, einheit, vorname, nachname, firma_name, status, kaltmiete, nebenkosten_vorauszahlung, heizkosten, weitere_kosten, miet_historie, mietbeginn, mietende';

async function lade(): Promise<EigListeDaten> {
  const [eigentuemer, objektEig, einheitEig, objekte, einheiten, mieter, profile, verguetung] = await Promise.all([
    alleSeiten<EigListeDaten['eigentuemer'][number]>((von, bis) => supabase.from('eigentuemer').select('*').order('nachname').order('id').range(von, bis)),
    alleSeiten<ObjektEigRoh>((von, bis) => supabase.from('objekt_eigentuemer').select('objekt_id, eigentuemer_id, vertrag_ab, vertrag_bis').order('objekt_id').order('eigentuemer_id').range(von, bis)),
    alleSeiten<EinheitEigRoh>((von, bis) => supabase.from('einheit_eigentuemer')
      .select('einheit_id, eigentuemer_id, mea, hausgeld_vorschuss, ruecklage_vorschuss, sev_status, vertrag_ab, vertrag_bis').order('einheit_id').range(von, bis)),
    alleSeiten<ObjektRoh>((von, bis) => supabase.from('objekte').select('*').eq('ist_verwaltung', false).order('objektnummer').range(von, bis)),
    alleSeiten<EinheitRoh>((von, bis) => supabase.from('objekt_einheiten').select('id, objekt_id, bezeichnung, wohnflaeche').order('id').range(von, bis)),
    alleSeiten<MieterRoh>((von, bis) => supabase.from('mieter').select(MIETER_FELDER).order('id').range(von, bis)),
    alleSeiten<{ email: string | null; status: string | null }>((von, bis) => supabase.from('profiles').select('email, status').eq('role', 'eigentuemer').order('id').range(von, bis)),
    alleSeiten<VerguetungRoh>((von, bis) => supabase.from('eigentuemer_verguetung').select('eigentuemer_id, betrag_je_einheit, gueltig_ab').order('id').range(von, bis)),
  ]);
  const portal = new Map<string, string | null>();
  for (const p of profile) {
    const mail = (p.email ?? '').trim().toLowerCase();
    if (mail && portal.get(mail) !== 'Aktiv') portal.set(mail, p.status);
  }
  return { eigentuemer, objektEig, einheitEig, objekte, einheiten, mieter, portal, verguetungen: verguetungJe(verguetung) };
}

export function useEigentuemerListe(): { zustand: EigListeZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<EigListeZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    let aktiv = true;
    lade()
      .then(daten => { if (aktiv) setZustand({ status: 'ok', daten }); })
      .catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [runde]);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);
  return { zustand, neuLaden };
}
